import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import _ from '@lodash';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import specializations from 'app/shared-components/controls/specializations.json';
import clientTypes from 'app/shared-components/controls/clientTypes.json';
import insurance from 'app/shared-components/controls/insurance.json';
import treatments from 'app/shared-components/controls/treatments.json';
import programTypes from 'app/shared-components/controls/programTypes.json';
import ControlledTextField from 'app/shared-components/form/ControlledTextField';
import ControlledMultiSelect from 'app/shared-components/form/ControlledMultiSelect';
import ControlledSingleSelect from 'app/shared-components/form/ControlledSingleSelect';
import ControlledCheckbox from 'app/shared-components/form/ControlledCheckbox';
import { selectProgramById, createProgram, updateProgram } from 'app/store/programsSlice';
import dispatchSubmit from 'app/shared-components/util/dispatchSubmit';
import ProgramModel from '../model/ProgramModel';
import EntityForm from 'app/shared-components/layout/EntityForm';

const defaultValues= ProgramModel();

/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
  name: yup.string().required('You must enter a name'),
  description: yup.string().required('You must enter a description'),
  type: yup.string().required('You must enter a type'),
  email: yup.string().email('You must enter a valid email'),
  website: yup.string(),
  clientTypes: yup.array(),
  specializations: yup.array(),
  treatments: yup.array(),
  acceptedInsurance: yup.array(),
  acceptingClients: yup.boolean().default(false),
  acceptsInsurance: yup.boolean().default(false),
  providesTelehealth: yup.boolean().default(false),
});

function ProgramForm(props) {
  const routeParams = useParams();
  const {groupId, programId} = routeParams;
  const isNew = programId === 'new';
  const program = isNew ? ProgramModel() : ProgramModel(useSelector(selectProgramById(programId)));
  const dispatch = useDispatch();
  const title= isNew ? "New Program" : "Edit " + program?.name;
  const returnPath= "/apps/organization/" + groupId + "/programs";

  const { control, watch, reset, handleSubmit, formState, getValues } = useForm({
	defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const { isValid, dirtyFields, errors } = formState;
  const watchAcceptsInsurance = watch("acceptsInsurance");

  useEffect(() => {
    reset({ ...program });
  }, [ programId, reset]);

  /**
   * Form Submit
   */
  function onSubmit(data) {
	const prog= { ...data, id: programId, organizationId: groupId, unread: undefined, locations: undefined};
	dispatchSubmit({dispatch: dispatch, 
	                submitFunction: updateProgram(prog), 
	                returnPath: returnPath, 
	                successMessage:  "Program Updated!"});
  }

  function onSubmitNew(data) {
    const prog= { ...data, organizationId: groupId};
	dispatchSubmit({dispatch: dispatch, 
	                submitFunction: createProgram(prog), 
	                returnPath: returnPath, 
	                successMessage:  "Program Created!"});	
  }

  return (
	<EntityForm title={title} 
	            submitTitle={isNew ? "Create" : "Update"} 
	            onSubmit={isNew ? onSubmitNew : onSubmit}
	            handleSubmit={handleSubmit} isValid={isValid} dirtyFields={dirtyFields}
	            returnPath={returnPath}
	>
        <ControlledTextField name="name" label="Program name"
                             control={control} errors={errors}
             				 autoFocus required fullWidth />
        
        <ControlledTextField name="description" label="Program description"
                             control={control} errors={errors}
             				 required fullWidth multiline
             				 minRows={3} maxRows={10} />
             				 
        <ControlledSingleSelect name="type" label="Program Type" 
                            control={control} errors={errors}
                            items={programTypes} maxHeight={300} /> 
        
        <ControlledTextField name="website" label="Website URL"
                             control={control} errors={errors}
             				 fullWidth />
        <ControlledTextField name="email" label="Email Address"
                             control={control} errors={errors}
             				 fullWidth />
             				
        <ControlledMultiSelect name="clientTypes" label="Client Types" 
                            control={control} errors={errors}
                            items={clientTypes} maxHeight={300} />       
         
        <ControlledMultiSelect name="treatments" label="Treatments" 
                            control={control} errors={errors}
                            items={treatments} maxHeight={300} />  
                            
		<ControlledMultiSelect name="specializations" label="Specializations" 
                            control={control} errors={errors}
                            items={specializations} maxHeight={400} />     
     
     
		<ControlledCheckbox name="acceptingClients" control={control} errors={errors}
		                    label="Accepting Clients"/> 

            

		<ControlledCheckbox name="providesTelehealth" control={control} errors={errors}
		                    label="Offers Telehealth"/>          
     
            
     
		<ControlledCheckbox name="acceptsInsurance" control={control} errors={errors}
		                    label="Accepts Insurance"/>         
       
            
        {watchAcceptsInsurance && (  
		<ControlledMultiSelect name="acceptedInsurance" label="Accepted Plans" className="mt-0 mb-0"
                            control={control} errors={errors}
                            items={insurance} />    
        )}
     				 
    </EntityForm>
      
  );
}

export default ProgramForm;
